/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { css } from 'aphrodite';
import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { colors, LoadingSpinner } from '@blend/consumer-components';

import { useVideoContext, VideoProvider } from '../../contexts/videoProvider/videoContext';
import { useRootSelector } from '../../hooks/useRootSelector';
import { setSelectedDocument } from '../../actions/documentActions';
import { ExitSessionConfirmationModal } from '../../components/ParticipantRoom/ExitSessionConfirmationModal/ExitSessionConfirmationModal';
import { usePreFetchVideoProvider } from '../../hooks/videoProvider/usePreFetchVideoProvider';
import { useRoom } from '../../hooks/useRoom';
import { SigningRoomNavigation } from './SigningRoomNavigation/SigningRoomNavigation';
import { styles } from './styles';
import { usePollParticipantCallDetails } from './hooks/usePollParticipantCallDetails';
import { useCallEndRedirect } from './hooks/useCallEndRedirect';
import { useDocuments } from './hooks/useDocuments';
import { usePollSigningAction } from './hooks/usePollSigningAction';
import { useConnectToVideoProvider } from './hooks/useConnectToVideoProvider';
import { usePollUsers } from './hooks/usePollUsers';
import { SigningRoomNavigationItemIds } from './SigningRoomNavigation/signingRoomItems';
import { SigningRoomPageContent } from './SigningRoomPageContent/SigningRoomPageContent';
import PendingTaskBanner from './PendingTaskBanner';
import { getSelectedDocumentPdfUrl } from '@utils/documents';
import { setRumSessionUserName } from '@utils/datadogRum';

export namespace ParticipantSigningRoomPage {
  export interface OwnProps {}
  export type Props = OwnProps;
}

export const _ParticipantSigningRoomPage: React.FC<ParticipantSigningRoomPage.Props> = ({}) => {
  const dispatch = useDispatch();
  const { videoProvider } = useVideoContext();
  const { apiKey } = useConnectToVideoProvider();
  const { callDetails, document, signingRoom, layout } = useRootSelector(reduxState => reduxState);
  const { documents } = document;
  const { selectedNavigationItemId } = signingRoom;
  const { isMobileView } = layout;
  const { roomId } = useRoom();

  setRumSessionUserName(roomId, 'participant-signing');
  useCallEndRedirect();
  useDocuments();
  usePollSigningAction();
  usePollParticipantCallDetails();
  usePollUsers();

  const ParticipantStrip = useMemo(() => {
    return videoProvider.getParticipantStripComponent();
  }, [videoProvider]);

  const setSelectedDocumentAndFetchPdf = useCallback(
    async (documentId: string) => {
      const fileUrl = await getSelectedDocumentPdfUrl(documentId, callDetails, documents, false);
      setSelectedDocument({
        id: documentId,
        pdfUrl: fileUrl,
      })(dispatch);
    },
    [callDetails, documents],
  );

  const showLoading = !callDetails;
  if (showLoading) {
    return (
      <div className={css(styles.pageLoadingSpinnerHolder)}>
        <LoadingSpinner size="lg" color={colors.catalyst} />
      </div>
    );
  }

  const isMobileParticipantsView =
    isMobileView && selectedNavigationItemId === SigningRoomNavigationItemIds.participants;
  const replaceParticipantTabWithStrip =
    videoProvider.shouldReplaceParticipantTabWithStrip() && isMobileParticipantsView;
  return (
    <div className={css(styles.signingRoomPageWrapper)}>
      <PendingTaskBanner roomId={roomId} isNotarySide={false} />
      <div className={css(styles.wrapper)} data-it="ParticipantSigningRoomPage">
        {(replaceParticipantTabWithStrip || !isMobileParticipantsView) && apiKey && (
          <ParticipantStrip isParticipantsTabView={replaceParticipantTabWithStrip} />
        )}
        <div className={css(styles.main, replaceParticipantTabWithStrip && styles.mainWithStrip)}>
          <SigningRoomNavigation handleDocumentClick={setSelectedDocumentAndFetchPdf} />
          <SigningRoomPageContent setSelectedDocumentAndFetchPdf={setSelectedDocumentAndFetchPdf} />
        </div>
        <ExitSessionConfirmationModal />
      </div>
    </div>
  );
};

export const ParticipantSigningRoomPage = (props: ParticipantSigningRoomPage.OwnProps) => {
  const { isLoading, videoProviderName } = usePreFetchVideoProvider();
  return !isLoading ? (
    <VideoProvider opts={{ dominantSpeaker: true }} videoProviderName={videoProviderName}>
      <_ParticipantSigningRoomPage {...props} />
    </VideoProvider>
  ) : null;
};
